import { ProductImageComponent } from '../shared/product-image/product-image.component'
import { PurchaseOrderService } from '../../services/purchase-order.service'
import { Router } from '@angular/router'
import { REQUISITION_STATUS } from '../../constants/requisition'
import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { ActivatedRoute, CanDeactivateFn, RouterLink } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { DatePipe } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { MatIconModule } from '@angular/material/icon'
import { Dialog } from '@angular/cdk/dialog'
import { firstValueFrom, Observable, of, Subject, switchMap, take } from 'rxjs'
import { SpinnerService } from '../../services/spinner.service'
import { MarketplaceService } from '../../services/marketplace.service'
import {
  Requisition,
  RequisitionApprover,
  RequisitionDecision,
  RequisitionItem,
  RequisitionService,
} from '../../services/requisition.service'
import { TranslationService } from '../../services/translation.service'
import { AuthService } from '../../services/auth.service'
import { RouteService } from '../../services/route.service'
import { ROUTES } from '../../constants/routes'
import { DialogService } from '../../services/dialog.service'
import { PaginatorIntl } from '../intl/paginator-intl'

@Component({
  selector: 'app-requisitions',
  imports: [
    ProductImageComponent,
    RouterLink,
    FormsModule,
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatIconModule,
  ],
  templateUrl: './requisitions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './requisitions.component.scss',
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
})
export class RequisitionsComponent implements OnInit {
  @ViewChildren('quantityInput') quantityInputs!: QueryList<
    ElementRef<HTMLInputElement>
  >

  private orders = inject(PurchaseOrderService)
  private router = inject(Router)
  purchaseOrderFailed = false
  downloadingJSON = false
  private api = inject(RequisitionService)
  private route = inject(ActivatedRoute)
  private auth = inject(AuthService)
  private routeService = inject(RouteService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private spinnerService = inject(SpinnerService)
  marketplace = inject(MarketplaceService)
  t = inject(TranslationService)
  isDetailRoute = !!this.route.snapshot.paramMap.get('id')
  request: Requisition | null = null
  requests: Requisition[] = []
  readonly requisitionStatus = REQUISITION_STATUS
  statusFilter = 'all'
  readonly statusFilters = [
    REQUISITION_STATUS.DRAFT,
    REQUISITION_STATUS.AWAITING_BUYER_REVIEW,
    REQUISITION_STATUS.APPROVED,
    REQUISITION_STATUS.PARTIALLY_APPROVED,
    REQUISITION_STATUS.REJECTED,
  ]
  pageIndex = 0
  pageSize = 10
  approvers: RequisitionApprover[] = []
  approversLoaded = false
  loading = true
  busy = false
  error = ''
  notice = ''
  unitConflict: { productId: string; currentUnit: string } | null = null
  reviewDecisions: Record<string, RequisitionDecision> = {}
  bulkRejectOpen = false
  bulkRejectionReason = ''
  reviewBlocked = false
  readonly = false
  itemPhotos: Record<string, string> = {}
  itemPhotosRevision = 0
  staleConflict = false
  private savedSnapshot = ''
  private saveSettled = new Subject<void>()

  get pageTitle(): string {
    if (!this.isDetailRoute) return this.t.getRequisitionSavedText()
    return this.request?.status === REQUISITION_STATUS.DRAFT
      ? this.t.getRequisitionDraftTitleText()
      : this.t.getRequisitionDetailTitleText()
  }

  draftFieldsDisabled(): boolean {
    return (
      this.readonly ||
      this.busy ||
      this.request?.status !== REQUISITION_STATUS.DRAFT
    )
  }

  statusIcon(status: string): string {
    switch (status) {
      case REQUISITION_STATUS.DRAFT:
        return 'edit_note'
      case REQUISITION_STATUS.AWAITING_BUYER_REVIEW:
        return 'pending_actions'
      case REQUISITION_STATUS.APPROVED:
        return 'check_circle'
      case REQUISITION_STATUS.PARTIALLY_APPROVED:
        return 'rule'
      case REQUISITION_STATUS.REJECTED:
        return 'cancel'
      default:
        return 'assignment'
    }
  }

  get filteredRequests(): Requisition[] {
    if (this.statusFilter === 'all') return this.requests
    if (this.statusFilter === 'awaiting_your_review') {
      return this.requests.filter((request) => this.canReview(request))
    }
    return this.requests.filter(
      (request) => request.status === this.statusFilter,
    )
  }

  filterChanged(): void {
    this.pageIndex = 0
  }

  clearFilter(): void {
    this.statusFilter = 'all'
    this.filterChanged()
  }

  get pagedRequests(): Requisition[] {
    const start = this.pageIndex * this.pageSize
    return this.filteredRequests.slice(start, start + this.pageSize)
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex
    this.pageSize = event.pageSize
  }

  waitUntilIdle(): Observable<void> {
    return this.busy ? this.saveSettled.pipe(take(1)) : of(undefined)
  }

  private finishBusy(): void {
    this.busy = false
    this.saveSettled.next()
  }

  hasUnsavedChanges(): boolean {
    if (this.canReview()) {
      return (
        (this.bulkRejectOpen && !!this.bulkRejectionReason.trim()) ||
        Object.values(this.reviewDecisions).some(
          (decision) => !!decision.outcome,
        )
      )
    }
    return (
      !!this.request &&
      this.request.status === REQUISITION_STATUS.DRAFT &&
      !this.readonly &&
      !!this.savedSnapshot &&
      JSON.stringify(this.request) !== this.savedSnapshot
    )
  }

  private captureSavedState(): void {
    this.savedSnapshot = JSON.stringify(this.request)
  }

  ngOnInit(): void {
    this.routeService.setRoute(
      this.routeService.getUserRoutes(
        ROUTES.REQUISITIONS.title,
        ROUTES.REQUISITIONS.path,
      ),
    )
    const id = this.route.snapshot.paramMap.get('id')
    if (id) {
      this.api.get(id).subscribe({
        next: (request) => {
          this.request = request
          this.loadItemPhotos(request.items)
          this.readonly =
            request.requesterId !== this.auth.getSessionFromLS()?.user.id
          this.initializeReview()
          this.captureSavedState()
          this.loading = false
          if (request.status === REQUISITION_STATUS.DRAFT && !this.readonly) {
            this.api.approvers().subscribe({
              next: (approvers) => {
                this.approvers = approvers
                this.approversLoaded = true
              },
              error: () => {
                this.error = this.t.getRequisitionApproversFailedText()
              },
            })
          }
        },
        error: () => {
          this.error = this.t.getRequisitionLoadFailedText()
          this.loading = false
        },
      })
    } else {
      this.api.list().subscribe({
        next: (requests) => {
          this.requests = requests
          this.loading = false
        },
        error: () => {
          this.error = this.t.getRequisitionLoadFailedText()
          this.loading = false
        },
      })
    }
  }

  canReview(request = this.request): boolean {
    const session = this.auth.getSessionFromLS()
    return (
      !!request &&
      request.status === REQUISITION_STATUS.AWAITING_BUYER_REVIEW &&
      request.approverId === session?.user.id &&
      request.requesterId !== session?.user.id &&
      session?.user.role === 'Admin'
    )
  }

  private initializeReview(): void {
    this.reviewDecisions = {}
    this.bulkRejectOpen = false
    this.bulkRejectionReason = ''
    this.reviewBlocked = false
    if (!this.canReview()) return
    for (const item of this.request?.items || []) {
      this.reviewDecisions[item.productId] = {
        productId: item.productId,
        outcome: '',
        approvedQuantity: item.requestedQuantity,
        rejectionReason: '',
      }
    }
  }

  approveAll(): void {
    if (!this.request || !this.canReview() || this.busy || this.reviewBlocked)
      return
    for (const item of this.request.items) {
      this.reviewDecisions[item.productId] = {
        productId: item.productId,
        outcome: REQUISITION_STATUS.APPROVED,
        approvedQuantity: item.requestedQuantity,
        rejectionReason: '',
      }
    }
    this.bulkRejectOpen = false
    this.bulkRejectionReason = ''
    this.error = ''
  }

  private validRejectionReason(reason: string): boolean {
    // immudb's VARCHAR limit and the API both count UTF-8 bytes.
    return !!reason.trim() && new TextEncoder().encode(reason).length <= 2048
  }

  canRejectAll(): boolean {
    return this.validRejectionReason(this.bulkRejectionReason)
  }

  rejectAll(): void {
    if (
      !this.request ||
      !this.canReview() ||
      this.busy ||
      this.reviewBlocked ||
      !this.canRejectAll()
    )
      return
    const reason = this.bulkRejectionReason.trim()
    for (const item of this.request.items) {
      this.reviewDecisions[item.productId] = {
        productId: item.productId,
        outcome: REQUISITION_STATUS.REJECTED,
        approvedQuantity: 0,
        rejectionReason: reason,
      }
    }
    this.bulkRejectOpen = false
    this.bulkRejectionReason = ''
    this.error = ''
  }

  decisionChanged(item: RequisitionItem): void {
    const decision = this.reviewDecisions[item.productId]
    decision.approvedQuantity =
      decision.outcome === REQUISITION_STATUS.REJECTED
        ? 0
        : item.requestedQuantity
    decision.rejectionReason = ''
    this.error = ''
  }

  validDecision(item: RequisitionItem): boolean {
    const decision = this.reviewDecisions[item.productId]
    if (!decision) return false
    if (decision.outcome === REQUISITION_STATUS.REJECTED) {
      return this.validRejectionReason(decision.rejectionReason)
    }
    return (
      decision.outcome === REQUISITION_STATUS.APPROVED &&
      decision.approvedQuantity !== null &&
      this.marketplace.validQuantity(
        decision.approvedQuantity,
        item.unitOfMeasure,
      ) &&
      decision.approvedQuantity <= item.requestedQuantity
    )
  }

  finalizeReview(): void {
    if (
      !this.request ||
      !this.canReview() ||
      this.busy ||
      this.reviewBlocked ||
      this.bulkRejectOpen
    )
      return
    if (!this.request.items.every((item) => this.validDecision(item))) {
      this.error = this.t.getRequisitionReviewValidationText()
      return
    }
    this.busy = true
    this.error = ''
    this.notice = ''
    this.api
      .review(
        this.request.id,
        this.request.revision,
        this.request.items.map((item) => this.reviewDecisions[item.productId]),
      )
      .subscribe({
        next: (reviewed) => {
          this.request = reviewed
          this.initializeReview()
          this.captureSavedState()
          this.notice = this.t.getRequisitionReviewedNoticeText()
          this.finishBusy()
        },
        error: (error) => {
          // On conflict or a lost response, reload before allowing another submission.
          this.reviewBlocked =
            error.status === 409 ||
            error.status === 403 ||
            error.status === 0 ||
            error.status >= 500
          this.purchaseOrderFailed =
            error.error?.reason === 'purchase_order_failed'
          if (this.purchaseOrderFailed) {
            this.api.get(this.request!.id).subscribe({
              next: (latest) => {
                this.request = latest
                this.initializeReview()
                this.captureSavedState()
                this.finishBusy()
              },
              error: () => {
                this.reviewBlocked = true
                this.finishBusy()
              },
            })
          }
          this.error = this.purchaseOrderFailed
            ? this.t.getPurchaseOrderCreateFailedText()
            : this.t.getRequisitionReviewErrorText(
                error.error?.reason,
                error.status,
              )
          if (!this.purchaseOrderFailed) this.finishBusy()
        },
      })
  }

  async downloadRequisitionJSON(): Promise<void> {
    const request = this.request
    if (
      !request ||
      this.downloadingJSON ||
      (request.status !== REQUISITION_STATUS.APPROVED &&
        request.status !== REQUISITION_STATUS.PARTIALLY_APPROVED)
    )
      return
    this.downloadingJSON = true
    this.spinnerService.startSpinner()
    try {
      const blob = await firstValueFrom(this.api.downloadApproved(request.id))
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      try {
        link.href = url
        link.download = `${request.reference}.json`
        link.hidden = true
        document.body.appendChild(link)
        link.click()
      } finally {
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      }
      this.dialogService.showDialog(
        this.dialog,
        this.t.getRequisitionJSONCompleteText(),
        this.t.getRequisitionJSONDownloadedText(),
        { width: 'min(550px, calc(100vw - 32px))', height: 'fit-content' },
      )
    } catch {
      this.dialogService.showDialog(
        this.dialog,
        this.t.getErrorText(),
        this.t.getRequisitionJSONFailedText(),
        { width: 'min(550px, calc(100vw - 32px))', height: 'fit-content' },
      )
    } finally {
      this.downloadingJSON = false
      this.spinnerService.stopSpinner()
    }
  }

  openPurchaseOrder(): void {
    if (!this.request || this.busy) return
    this.busy = true
    this.error = ''
    this.orders.retry(this.request.id).subscribe({
      next: (order) => {
        this.finishBusy()
        this.router.navigate(['/purchase-orders', order.id])
      },
      error: () => {
        this.purchaseOrderFailed = true
        this.error = this.t.getPurchaseOrderCreateFailedText()
        this.finishBusy()
      },
    })
  }

  reloadReview(): void {
    if (!this.request || this.busy) return
    this.busy = true
    this.api.get(this.request.id).subscribe({
      next: (latest) => {
        this.request = latest
        this.initializeReview()
        this.captureSavedState()
        this.error = ''
        this.finishBusy()
      },
      error: () => {
        this.error = this.t.getRequisitionLoadFailedText()
        this.finishBusy()
      },
    })
  }

  /**
   * Reloads card photo URLs, including retained items whose photo was replaced.
   * @param items Requisition items whose product photos should be refreshed.
   * @returns Nothing; updates the item photos as each request completes.
   */
  private loadItemPhotos(items: RequisitionItem[]): void {
    // A replacement keeps the same URL. Refresh retained image components too.
    this.itemPhotos = {}
    this.itemPhotosRevision++
    for (const item of items) {
      this.marketplace.get(item.productId).subscribe({
        next: (product) => {
          if (product.picture) this.itemPhotos[item.productId] = product.picture
        },
        error: () => {
          // The saved requisition remains usable if a product is no longer listed.
        },
      })
    }
  }

  validQuantity(item: RequisitionItem): boolean {
    return this.marketplace.validQuantity(
      Number(item.requestedQuantity),
      item.unitOfMeasure,
    )
  }

  hasInvalidQuantity(): boolean {
    return (
      this.request?.items.some((item) => !this.validQuantity(item)) || false
    )
  }

  clearQuantityError(): void {
    if (
      this.error === this.t.getRequisitionInvalidQuantityText() &&
      !this.hasInvalidQuantity()
    ) {
      this.error = ''
    }
  }

  hasValidApprover(): boolean {
    return (
      !!this.request?.approverId &&
      this.approvers.some(
        (approver) => approver.id === this.request?.approverId,
      )
    )
  }

  canSubmit(): boolean {
    const request = this.request
    return (
      !!request &&
      request.status === REQUISITION_STATUS.DRAFT &&
      !this.readonly &&
      !!request.items.length &&
      !this.hasInvalidQuantity() &&
      !!request.recipient.trim() &&
      !!(request.deliveryAddress.trim() || request.collectionLocation.trim()) &&
      this.hasValidApprover()
    )
  }

  totals(): { currency: string; amount: number }[] {
    const totals = new Map<string, number>()
    for (const item of this.request?.items || []) {
      if (item.priceAmount !== null && item.priceCurrency) {
        totals.set(
          item.priceCurrency,
          (totals.get(item.priceCurrency) || 0) +
            item.priceAmount * item.requestedQuantity,
        )
      }
    }
    return [...totals].map(([currency, amount]) => ({ currency, amount }))
  }

  unpricedCount(): number {
    return (
      this.request?.items.filter(
        (item) => item.priceAmount === null || !item.priceCurrency,
      ).length || 0
    )
  }

  formatPrice(amount: number, currency: string): string {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      }).format(amount)
    } catch {
      return `${amount} ${currency}`
    }
  }

  save(submit = false, acceptedUnitChanges: Record<string, string> = {}): void {
    if (
      !this.request ||
      this.busy ||
      this.staleConflict ||
      this.readonly ||
      this.request.status !== REQUISITION_STATUS.DRAFT
    )
      return
    const invalidIndex = this.request.items.findIndex(
      (item) => !this.validQuantity(item),
    )
    if (invalidIndex !== -1) {
      this.error = this.t.getRequisitionInvalidQuantityText()
      this.quantityInputs.get(invalidIndex)?.nativeElement.focus()
      return
    }
    if (submit && !this.canSubmit()) {
      this.error = this.approvers.length
        ? this.t.getRequisitionMissingDetailsText()
        : this.t.getRequisitionNoApproverText()
      return
    }
    this.busy = true
    this.error = ''
    this.notice = ''
    this.unitConflict = null
    this.api.save(this.request, acceptedUnitChanges).subscribe({
      next: (saved) => {
        this.request = saved
        this.captureSavedState()
        if (submit) this.submitSaved(saved.id, saved.revision)
        else {
          this.notice = this.t.getRequisitionSavedNoticeText()
          this.finishBusy()
        }
      },
      error: (error) => {
        if (error.error?.reason === 'unit_changed') {
          this.unitConflict = {
            productId: error.error.productId,
            currentUnit: error.error.currentUnit,
          }
        }
        if (
          error.error?.reason === 'already_submitted' ||
          error.error?.reason === 'stale_draft'
        ) {
          this.staleConflict = true
        }
        this.error = this.t.getRequisitionErrorText(error.error?.reason)
        this.finishBusy()
      },
    })
  }

  loadLatest(): void {
    if (!this.request || this.busy || !this.staleConflict) return
    this.dialogService
      .confirmDialog(
        this.dialog,
        this.t.getRequisitionLoadLatestTitleText(),
        this.t.getRequisitionLoadLatestWarningText(),
        {
          cancelText: this.t.getRequisitionKeepEditingText(),
          confirmText: this.t.getRequisitionDiscardAndLoadText(),
        },
      )
      .subscribe((confirmed) => {
        if (!confirmed || !this.request) return
        this.busy = true
        this.api.get(this.request.id).subscribe({
          next: (latest) => {
            this.request = latest
            this.captureSavedState()
            this.loadItemPhotos(latest.items)
            this.staleConflict = false
            this.error = ''
            this.notice = this.t.getRequisitionLatestLoadedText()
            this.finishBusy()
          },
          error: () => {
            this.error = this.t.getRequisitionLoadFailedText()
            this.finishBusy()
          },
        })
      })
  }

  acceptCurrentUnit(): void {
    const conflict = this.unitConflict
    if (!conflict) return
    this.save(false, { [conflict.productId]: conflict.currentUnit })
  }

  unitConflictProductName(): string {
    const productId = this.unitConflict?.productId
    return (
      this.request?.items.find((item) => item.productId === productId)
        ?.productName ||
      productId ||
      ''
    )
  }

  private submitSaved(id: string, revision: number): void {
    this.api.submit(id, revision).subscribe({
      next: (submitted) => {
        this.request = submitted
        this.captureSavedState()
        this.notice = this.t.getRequisitionSubmittedNoticeText()
        this.finishBusy()
      },
      error: (error) => {
        if (error.error?.reason === 'unit_changed') {
          this.unitConflict = {
            productId: error.error.productId,
            currentUnit: error.error.currentUnit,
          }
        }
        this.api.get(id).subscribe({
          next: (latest) => {
            if (latest.status === REQUISITION_STATUS.AWAITING_BUYER_REVIEW) {
              this.request = latest
              this.captureSavedState()
              this.error = ''
              this.notice = this.t.getRequisitionSubmittedNoticeText()
            } else {
              if (error.error?.reason === 'stale_draft') {
                this.staleConflict = true
              }
              this.error = this.t.getRequisitionErrorText(error.error?.reason)
            }
            this.finishBusy()
          },
          error: () => {
            this.error = this.t.getRequisitionSubmitUncertainText()
            this.finishBusy()
          },
        })
      },
    })
  }

  removeItem(item: RequisitionItem): void {
    if (this.request) {
      this.request.items = this.request.items.filter((i) => i !== item)
      this.notice = ''
    }
  }
}

export const requisitionUnsavedChangesGuard: CanDeactivateFn<
  RequisitionsComponent
> = (component) => {
  const dialogService = inject(DialogService)
  const dialog = inject(Dialog)
  return component.waitUntilIdle().pipe(
    switchMap(() =>
      component.hasUnsavedChanges()
        ? dialogService.confirmDialog(
            dialog,
            component.t.getRequisitionUnsavedTitleText(),
            component.canReview()
              ? component.t.getRequisitionReviewUnsavedText()
              : component.t.getRequisitionUnsavedWarningText(),
            {
              cancelText: component.t.getRequisitionKeepEditingText(),
              confirmText: component.t.getRequisitionLeaveText(),
            },
          )
        : of(true),
    ),
  )
}
